import { useState } from 'react';
import type { EngagementResult } from '@exigo/shared';
import { formatCount, formatMoney } from '@exigo/shared';
import { Badge } from '../../components/ui/Badge';
import styles from './EngagementRow.module.css';

interface EngagementRowProps {
  row: EngagementResult;
  fx: number; // AUD per USD in micro-units, 0 = unset
}

/** One engagement — chips, payout, gate summary, and an expandable detail panel. */
export function EngagementRow({ row, fx }: EngagementRowProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const audValue = fx > 0 && row.payoutUsd !== null ? Math.round((row.payoutUsd * fx) / 1_000_000) : null;

  return (
    <div className={`${styles.row} ${row.eligible ? styles.rowOn : ''}`}>
      <button type="button" className={styles.head} aria-expanded={open} onClick={() => { setOpen((v) => !v); }}>
        <div className={styles.headLeft}>
          <div className={styles.chips}>
            {row.pick === 'recommended' && <Badge tone="primary">Recommended</Badge>}
            {row.pick === 'alternative' && <Badge tone="neutral">Alternative</Badge>}
            <Badge tone="neutral">{row.phase}</Badge>
            {row.tier && <Badge tone="neutral">{row.tier.size}</Badge>}
            {row.tier?.minHours && <Badge tone="neutral">{row.tier.minHours}h min</Badge>}
            {row.eligible && row.perHourUsd !== null && (
              <Badge tone="ok">{formatMoney(row.perHourUsd)}/hr</Badge>
            )}
          </div>
          <p className={styles.name}>{row.name}</p>
        </div>

        <div className={styles.pay}>
          <p className={styles.payUsd}>
            {formatMoney(row.payoutUsd)} <span style={{ fontSize: 11, fontWeight: 600 }}>USD</span>
          </p>
          <p className={styles.payAud}>{audValue !== null ? `${formatMoney(audValue)} AUD` : '—'}</p>
          {row.stageTwoUsd !== null && (
            <p className={styles.payAud}>+{formatMoney(row.stageTwoUsd)} stage 2, conditional</p>
          )}
        </div>
      </button>

      {/*
        Gates sit outside the collapsible section, with their detail, because
        which gate is closing an engagement is the single most useful thing on
        the row — in the guide it is visible without expanding anything. Outside
        the <button> rather than inside it because a list of this length is not
        legal button content and would not be reachable by a screen reader.
      */}
      <ul className={styles.gateList}>
        {row.gates.map((g, i) => (
          <li key={`${g.label}-${String(i)}`} className={styles.gateItem}>
            <span className={g.ok ? styles.gateOk : styles.gateFail} aria-hidden="true">
              {g.ok ? '✓' : '✗'}
            </span>{' '}
            <strong>{g.label}</strong> — {g.detail}
          </li>
        ))}
      </ul>

      {open && (
        <div className={styles.detail}>
          <p className={styles.eyebrow}>Qualifying workloads</p>
          <p className={styles.detailBody}>{row.workloads}</p>

          {row.sizingLabel && (
            <>
              <p className={styles.eyebrow}>Sizing</p>
              <p className={styles.detailBody}>{row.sizingLabel}</p>
            </>
          )}

          {row.eligible && (
            <>
              <p className={styles.eyebrow}>Commercial read</p>
              <p className={styles.detailBody}>
                {row.yieldPct !== null && `${row.yieldPct.toFixed(1)}% of net new consumption`}
                {row.yieldPct !== null && row.perHourUsd !== null && ' · '}
                {row.perHourUsd !== null && `${formatMoney(row.perHourUsd)} per guide hour`}
                {row.yieldPct === null && row.perHourUsd === null && '—'}
              </p>
            </>
          )}

          <p className={styles.eyebrow}>Bands</p>
          <table className={styles.bandsTable}>
            <thead>
              <tr>
                <th>Size</th>
                <th>Range</th>
                <th>Market A</th>
                <th>Market B</th>
                {row.bands[0]?.marketC !== null && <th>Market C</th>}
              </tr>
            </thead>
            <tbody>
              {row.bands.map((b) => (
                <tr key={b.size} className={b.isHit ? styles.bandHit : undefined}>
                  <td>{b.size}</td>
                  <td>
                    {row.basis === 'acr' || row.basis === 'acv' ? formatMoney(b.floor) : formatCount(b.floor)}
                    {' – '}
                    {b.ceiling !== null
                      ? row.basis === 'acr' || row.basis === 'acv'
                        ? formatMoney(b.ceiling)
                        : formatCount(b.ceiling)
                      : '+'}
                  </td>
                  <td>{formatMoney(b.marketA)}</td>
                  <td>{formatMoney(b.marketB)}</td>
                  {b.marketC !== null && <td>{formatMoney(b.marketC)}</td>}
                </tr>
              ))}
            </tbody>
          </table>

          {row.duration && (
            <>
              <p className={styles.eyebrow}>Engagement duration</p>
              <p className={styles.detailBody}>{row.duration}</p>
            </>
          )}

          {row.poe.length > 0 && (
            <>
              <p className={styles.eyebrow}>Proof of execution</p>
              <ul className={styles.list}>
                {row.poe.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </>
          )}

          {row.notes.length > 0 && (
            <>
              <p className={styles.eyebrow}>Watch for</p>
              <ul className={styles.list}>
                {row.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </>
          )}

        </div>
      )}
    </div>
  );
}
